import omitBy from "lodash/omitBy";
import isUndefined from "lodash/isUndefined";
import isNull from "lodash/isNull";
import { api } from "../axios";
import {
  ApproveHolderProfilePayload,
  SendHolderAuthenticationOtpPayload,
  CreateOrLinkHolderProfilePayload,
  GetInvestorProfilesParams,
  HolderProfileResponse,
  InvestorProfileDetails,
  InvestorAddressDetails,
  InvestorBankDetail,
  InvestorNomineeDetails,
  InvestorDistributorMapping,
  InvestmentAccount,
  InvestorContactInfo,
  UpdateInvestorProfilePayload,
} from "../../types";

export const getInvestorProfileApi = (
  params: GetInvestorProfilesParams
): Promise<InvestorProfileDetails[]> =>
  api.get("investor/profiles/", { params });

export const createInvestorProfileApi = (
  payload: Partial<InvestorProfileDetails>
) => {
  const newPayload = omitBy(
    payload,
    (v) => isUndefined(v) || isNull(v) || v === ""
  );

  return api.post("investor/profiles/", {
    ...newPayload,
    type: "individual",
    tax_status: "resident_individual",
    use_default_tax_residences: true,
  });
};

export const createOrLinkHolderProfileApi = (
  payload: CreateOrLinkHolderProfilePayload
): Promise<HolderProfileResponse> => {
  const newPayload = omitBy(
    payload,
    (v) => isUndefined(v) || isNull(v) || v === ""
  );

  return api.post("investor/profiles/", newPayload);
};

export const approveHolderProfileApi = (
  payload: ApproveHolderProfilePayload
): Promise<HolderProfileResponse> =>
  api.post("investor/profiles/approve/", payload);

export const sendHolderAuthenticationOtpApi = (
  payload: SendHolderAuthenticationOtpPayload
) => api.post("investor/profiles/approve/send-authentication-otp/", payload);

export const updateInvestorProfileApi = (
  payload: UpdateInvestorProfilePayload
) => {
  const newPayload = omitBy(
    payload,
    (v) => isUndefined(v) || isNull(v) || v === ""
  );

  return api.patch("investor/profiles/", newPayload);
};

export const getInvestorAddressDetailsApi = (
  investmentAccountId: string
): Promise<[InvestorAddressDetails & { address_guid: string }]> =>
  api.get(`investor/address-info/`, {
    params: { investment_account_guid: investmentAccountId },
  });

export const createInvestorAddressDetailsApi = (
  payload: Omit<
    InvestorAddressDetails,
    "investment_account" | "address_guid" | "autofilled"
  >
) => api.post("investor/address-info/", payload);

export const updateInvestorAddressDetailsApi = (
  payload: Partial<InvestorAddressDetails>
) => api.put("investor/address-info/", payload);

export const createInvestorContactInfoApi = (
  payload: Partial<InvestorContactInfo>
) => api.post("investorcontact-info/", payload);

export const getInvestorContactInfoApi = (
  investmentAccountId?: string
): Promise<InvestorContactInfo[]> =>
  api.get(`investorcontact-info/`, {
    params: { investment_account_guid: investmentAccountId },
  });

export const updateInvestorContactInfoApi = (
  payload: Partial<InvestorContactInfo>
) => api.put("investorcontact-info/", payload);

export const getInvestorBasicInfo = (
  phoneNumber: string
): Promise<{ investor: InvestorDistributorMapping }[]> =>
  api.get(
    `investor-distributor-mappings/?phone_number=${encodeURIComponent(
      phoneNumber
    )}`
  );

export const getInvestorBankDetailsApi = (
  investmentAccountId: string
): Promise<InvestorBankDetail[]> =>
  api.get(`investor/bank-accounts/`, {
    params: { investment_account_guid: investmentAccountId },
  });

type CreateInvestorBankDetailsPayload = Omit<
  Partial<InvestorBankDetail>,
  "bank_account_guid" | "investment_account" | "id"
> & { investment_account_guid: string };

export const createInvestorBankDetailsApi = (
  payload: CreateInvestorBankDetailsPayload
) => api.post("investor/bank-accounts/", payload);

type UpdateInvestorBankDetailsPayload = Omit<
  Partial<InvestorBankDetail>,
  "investment_account"
> & { investment_account_guid: string };

export const updateInvestorBankDetailsApi = (
  payload: UpdateInvestorBankDetailsPayload
) => api.put("investor/bank-accounts/", payload);

export const getInvestorNomineeDetailsApi = (
  investmentAccountId: string
): Promise<(InvestorNomineeDetails & { id: string })[]> =>
  api.get(`nominees/`, {
    params: { investment_account_guid: investmentAccountId },
  });

interface CreateInvestorNomineeDetailsPayload {
  name: string;
  relationship: string;
  date_of_birth?: string | null;
  investment_account_guid: string;
  applicable_percentage: number;
  // Only name and relationship are mandatory. The rest are captured in the
  // account centre and are optional everywhere else.
  identity_type?: string;
  identity_number?: string;
  email?: string;
  mobile?: string;
  address_line_1?: string;
  address_line_2?: string | null;
  address_line_3?: string | null;
  city?: string;
  pin_code?: string;
  country?: string;
  guardian_name?: string | null;
  guardian_pan?: string | null;
}

export const createInvestorNomineeDetailsApi = (
  payload: CreateInvestorNomineeDetailsPayload
): Promise<InvestorNomineeDetails> => api.post("nominees/", payload);

export const updateInvestorNomineeDetailsApi = (
  payload: Omit<
    CreateInvestorNomineeDetailsPayload,
    "investment_account_guid"
  > & {
    id: string;
  }
): Promise<InvestorNomineeDetails> => {
  const { id, ...rest } = payload;
  if (!id) throw new Error("Nominee id is required");
  return api.put(`nominees/${id}/`, rest);
};

export const activateInvestmentAccount = (payload: { id: string }) =>
  api.post("investment-accounts/activate/", payload);

export const updateInvestmentAccount = (payload: {
  id: string;
  data: {
    nomination_opt: "N" | "Y";
    nominee_soa_opt?: "N" | "Y";
  };
}) =>
  api.put("investment-accounts/", {
    investment_account_guid: payload.id,
    ...payload.data,
  });

export const getInvestorInvestmentAccountApi = (
  userGuid?: string,
  investmentAccountId?: string
): Promise<InvestmentAccount[]> => {
  const params = omitBy(
    {
      user_guid: userGuid,
      investment_account_guid: investmentAccountId,
    },
    (v) => isUndefined(v) || isNull(v) || v === ""
  );
  return api.get(`investment-accounts/`, {
    params,
  });
};

export const createInvestorAccountApi = (payload: {
  pan: string;
  arn_code: string;
}) => api.post("investment-accounts/", payload);

export const notifyInvestorApi = (investmentAccountId: string) =>
  api.post("request-investor-verification/", {
    investment_account_guid: investmentAccountId,
  });

export const uploadElogApi = (payload: {
  investment_account_guid: string;
  otp: string;
  investor_ip: string;
}) => {
  return api.post("elog/upload/", payload);
};

export const verifyBankAccount = (investmentAccountGuid: string) =>
  api.post("bank-account-verification/", {
    investment_account_guid: investmentAccountGuid,
  });

type VerifyBankAccountResponse = {
  status:
    | "verified"
    | "verification_initiated"
    | "failed"
    | "name_mismatch"
    | "verification_initiated"
    | "in_process"
    | "no_change"
    | "limit_reached";
  message: string;
};

export const verifyProposedBankDetails = (payload: {
  investmentAccountGuid: string;
  accountNumber?: string;
  accountHolderName?: string;
  ifscCode?: string;
}): Promise<VerifyBankAccountResponse> =>
  api.get("bank-account-verification/", {
    params: {
      investment_account_guid: payload.investmentAccountGuid,
      account_number: payload.accountNumber,
      primary_account_holder_name: payload.accountHolderName,
      ifsc_code: payload.ifscCode,
    },
  });

type KycStatusResponse = {
  entry_date: string;
  kra_name: string;
  kyc_status: "S" | "F" | "U";
  kyc_status_remark: string;
  modification_date: string;
  name: string;
  pan: string;
  status_date: string;
};

export const checkKYCStatusApi = (
  payload: { client_id?: number | string; pan?: string } | number | string
): Promise<KycStatusResponse> => {
  const body =
    typeof payload === "object" && payload !== null
      ? payload
      : { client_id: payload };
  return api.post("kyc/check-status/", body);
};

interface RegisterKycResponse {
  entry_date: string;
  kyc_status: string;
  kyc_status_remark: string;
  email: string;
  link: string;
}

export const registerKYCApi = (
  clientId: number | string
): Promise<RegisterKycResponse> =>
  api.post("kyc/register/", { client_id: clientId });

interface SavedUPIAddressesResponse {
  upi_addresses: string[];
}
export const getSavedUPIAddressesApi = (
  investmentAccountId: string
): Promise<SavedUPIAddressesResponse> =>
  api.get("payments/saved_upi_addresses/", {
    params: { investment_account_guid: investmentAccountId },
  });

export const createInvestmentAccount = (clientId: number) => {
  return api.post("create-account/", {
    client_id: clientId,
  });
};

export async function getRiskProfileQuestions(): Promise<
  {
    id: number;
    question_text: string;
    options: { id: number; text: string }[];
  }[]
> {
  const response = await api.get("risk-profile-questions/");
  const questions = response.results || response;
  // @ts-expect-error implicit any
  return questions.map((q) => ({
    ...q,
    options: Object.entries<{ text: string; id?: string }>(q.options).map(
      ([k, v]) => ({
        ...v,
        id: v.id || k,
      })
    ),
  }));
}

export type RiskProfileTier = "CONSERVATIVE" | "MODERATE" | "AGGRESSIVE";

export function submitRiskProfile(payload: {
  clientId: number;
  responses: { questionId: number; optionId: string }[];
}): Promise<{
  risk_profile: RiskProfileTier;
  investment_account_id: string;
  id: number;
  status: string;
}> {
  return api.post("risk-profile-responses/", {
    client_id: payload.clientId,
    responses: payload.responses.map((r) => ({
      question_id: r.questionId,
      option_id: r.optionId,
    })),
  });
}

export function sendRiskProfileQuestionnare(clientId: number) {
  return api.post("risk-profile-questions/send-link/", {
    client_id: clientId,
  });
}
